import React, { useEffect, useState, useRef } from 'react';
import { useFloatingChat } from '../../context/FloatingChatContext';
import { useAuth } from '../../context/AuthContext';
import { X, Minus, Expand, Shrink, Users, CornerUpLeft } from 'lucide-react';
import { collection, query, orderBy, onSnapshot, addDoc, updateDoc, doc, Timestamp, getDocs, where } from 'firebase/firestore';
import { db } from '../../lib/firebase/config';
import MessageInput from './finChatsInput';
import MessageBubble from './finChatsBubble';
import { toast } from 'react-hot-toast';
import { ClaireGuideButton } from '../claire/ClaireGuideButton';
import { isClaireEmail } from '../../lib/claire/guide';
import { chatNotificationService } from '../../services/chatNotificationService';
import { cn } from '../../utils/cn';

interface ReplyTo {
  messageId: string;
  text: string;
  sender: string;
  senderName?: string;
}

interface Message {
  id: string;
  sender: string;
  text: string;
  imageUrl?: string;
  attachment?: {
    url: string;
    fileName: string;
    fileSize: number;
    fileType: string;
    uploadPath: string;
  };
  timestamp: Timestamp;
  editedAt?: Timestamp;
  isEdited?: boolean;
  replyTo?: ReplyTo;
}

interface Person {
  name: string;
  photoURL?: string;
}

const GROUP_GAP_MS = 5 * 60 * 1000;

const Avatar: React.FC<{ person?: Person; group?: boolean; size?: number }> = ({ person, group, size = 32 }) => {
  const [broken, setBroken] = useState(false);
  const style = { width: size, height: size };
  if (group) {
    return (
      <div className="flex flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500" style={style}>
        <Users style={{ width: size * 0.5, height: size * 0.5 }} />
      </div>
    );
  }
  if (person?.photoURL && !broken) {
    return <img src={person.photoURL} alt="" onError={() => setBroken(true)} className="flex-shrink-0 rounded-full object-cover" style={style} />;
  }
  return (
    <div
      className="flex flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#c9a46a] to-[#7a5a3a] font-semibold text-white"
      style={{ ...style, fontSize: size * 0.4 }}
    >
      {(person?.name || '?').charAt(0).toUpperCase()}
    </div>
  );
};

const FloatingChatWindow: React.FC = () => {
  const { floatingChat, deactivateFloatingChat, toggleCollapsed } = useFloatingChat();
  const { currentUser } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [lastSeenMessageCount, setLastSeenMessageCount] = useState(0);
  const [replyToMessage, setReplyToMessage] = useState<Message | null>(null);
  const [participants, setParticipants] = useState<string[]>([]);
  const [people, setPeople] = useState<Record<string, Person>>({});
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [tall, setTall] = useState(() => localStorage.getItem('floatingChatTall') === '1');
  const toggleTall = () =>
    setTall((t) => {
      localStorage.setItem('floatingChatTall', t ? '0' : '1');
      return !t;
    });

  const chatId = floatingChat.chatId || '';
  const myEmail = (currentUser?.email || '').toLowerCase();
  const others = participants.filter((p) => p.toLowerCase() !== myEmail);
  const isGroup = others.length > 1;
  const other = others.length === 1 ? people[others[0].toLowerCase()] : undefined;
  const title =
    floatingChat.chatData?.name ||
    (isGroup ? others.map((e) => people[e.toLowerCase()]?.name || e.split('@')[0]).join(', ') : other?.name) ||
    floatingChat.chatData?.displayName ||
    'Chat';
  const subtitle = isGroup ? `${participants.length} people` : 'FIN message';

  const getDisplayName = (email: string): string => people[email.toLowerCase()]?.name || email.split('@')[0] || email;

  // Messages
  useEffect(() => {
    if (!chatId || !floatingChat.isActive) return;
    setLoading(true);
    setReplyToMessage(null);
    const messagesQuery = query(collection(db, 'chats', chatId, 'messages'), orderBy('timestamp', 'asc'));
    return onSnapshot(messagesQuery, (snapshot) => {
      setMessages(snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as Message)));
      setLoading(false);
    });
  }, [chatId, floatingChat.isActive]);

  // Who's in the chat
  useEffect(() => {
    if (!chatId || !floatingChat.isActive) return;
    return onSnapshot(doc(db, 'chats', chatId), (snap) => setParticipants(snap.data()?.participants || []));
  }, [chatId, floatingChat.isActive]);

  // Names and photos for everyone in it
  useEffect(() => {
    const missing = participants.map((p) => p.toLowerCase()).filter((p) => !people[p]);
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      const found: Record<string, Person> = {};
      for (let i = 0; i < missing.length; i += 10) {
        const batch = missing.slice(i, i + 10);
        try {
          const snap = await getDocs(query(collection(db, 'users'), where('email', 'in', batch)));
          snap.docs.forEach((d) => {
            const u = d.data();
            found[String(u.email).toLowerCase()] = { name: u.displayName || String(u.email).split('@')[0], photoURL: u.photoURL || undefined };
          });
        } catch (err) {
          console.warn('Could not load chat participants', err);
        }
        batch.forEach((e) => {
          if (!found[e]) found[e] = { name: e.split('@')[0] };
        });
      }
      if (!cancelled) setPeople((prev) => ({ ...prev, ...found }));
    })();
    return () => {
      cancelled = true;
    };
  }, [participants, people]);

  const sendMessage = async (text: string, imageUrl?: string, attachment?: any) => {
    if (!chatId || !currentUser?.email || (!text.trim() && !imageUrl && !attachment)) return;
    try {
      const message: any = {
        sender: currentUser.email,
        text: text.trim(),
        ...(imageUrl && { imageUrl }),
        ...(attachment && { attachment }),
        timestamp: Timestamp.now(),
      };
      if (replyToMessage) {
        message.replyTo = {
          messageId: replyToMessage.id,
          text: replyToMessage.text?.substring(0, 100) || (replyToMessage.imageUrl ? '📷 Image' : '📎 Attachment'),
          sender: replyToMessage.sender,
          senderName: getDisplayName(replyToMessage.sender),
        };
      }
      await addDoc(collection(db, 'chats', chatId, 'messages'), message);

      let lastMessage = text.trim();
      if (imageUrl && !text.trim()) lastMessage = '📷 Image';
      else if (attachment && !text.trim()) lastMessage = `📎 ${attachment.fileName}`;
      else if (attachment) lastMessage = `📎 ${attachment.fileName}: ${text.trim()}`;
      await updateDoc(doc(db, 'chats', chatId), {
        lastMessage,
        lastMessageSender: currentUser.email,
        updatedAt: Timestamp.now(),
      });
      setReplyToMessage(null);
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    }
  };

  const editMessage = async (messageId: string, newText: string) => {
    if (!chatId || !currentUser?.email || !newText.trim()) return;
    try {
      await updateDoc(doc(db, 'chats', chatId, 'messages', messageId), {
        text: newText.trim(),
        editedAt: Timestamp.now(),
        isEdited: true,
      });
      toast.success('Message updated');
    } catch (error) {
      console.error('Error editing message:', error);
      toast.error('Failed to edit message');
    }
  };

  // Unread count while minimized; reading it clears FIN's chat notifications too.
  useEffect(() => {
    if (floatingChat.isCollapsed && messages.length > lastSeenMessageCount) {
      setUnreadCount(messages.length - lastSeenMessageCount);
    } else if (!floatingChat.isCollapsed) {
      setUnreadCount(0);
      setLastSeenMessageCount(messages.length);
      if (chatId && currentUser?.email && messages.length) chatNotificationService.markChatAsRead(currentUser.email, chatId);
    }
  }, [messages.length, floatingChat.isCollapsed, lastSeenMessageCount, chatId, currentUser?.email]);

  useEffect(() => {
    if (!floatingChat.isCollapsed && messages.length > 0 && messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages, floatingChat.isCollapsed, tall]);

  if (!floatingChat.isActive || !floatingChat.chatId) {
    return null;
  }

  const collapsed = floatingChat.isCollapsed;
  const iconButton = 'flex h-8 w-8 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors';

  if (collapsed) {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={toggleCollapsed}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && toggleCollapsed()}
        className="fixed bottom-6 right-6 z-[9999] flex w-[260px] cursor-pointer items-center gap-3 rounded-full border border-gray-200 bg-white py-2 pl-2 pr-2 text-left shadow-xl hover:shadow-2xl transition-shadow"
      >
        <span className="relative">
          <Avatar person={other} group={isGroup} size={36} />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white ring-2 ring-white">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13.5px] font-semibold text-gray-900">{title}</span>
          <span className={cn('block truncate text-[11.5px]', unreadCount > 0 ? 'font-medium text-red-600' : 'text-gray-500')}>
            {unreadCount > 0 ? `${unreadCount} new message${unreadCount > 1 ? 's' : ''}` : 'Tap to open'}
          </span>
        </span>
        <button
          onClick={(e) => {
            e.stopPropagation();
            deactivateFloatingChat();
          }}
          className={iconButton}
          title="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  const firstName = (other?.name || title).split(' ')[0];

  return (
    <div
      className="fixed bottom-6 right-6 z-[9999] flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl"
      style={{
        width: tall ? 'min(460px, calc(100vw - 3rem))' : 'min(380px, calc(100vw - 3rem))',
        height: tall ? 'calc(100vh - 3rem)' : 'min(540px, calc(100vh - 3rem))',
      }}
    >
      <div className="flex h-14 shrink-0 items-center gap-2.5 border-b border-gray-100 pl-3 pr-1.5">
        <Avatar person={other} group={isGroup} size={34} />
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-[14px] font-semibold leading-tight text-gray-900">{title}</h3>
          <p className="truncate text-[11.5px] leading-tight text-gray-500">{subtitle}</p>
        </div>
        {(messages.some((m) => isClaireEmail(m.sender)) || others.some((e) => isClaireEmail(e))) && <ClaireGuideButton />}
        <button onClick={toggleTall} className={iconButton} title={tall ? 'Make smaller' : 'Make bigger'}>
          {tall ? <Shrink className="h-4 w-4" /> : <Expand className="h-4 w-4" />}
        </button>
        <button onClick={toggleCollapsed} className={iconButton} title="Minimize">
          <Minus className="h-4 w-4" />
        </button>
        <button onClick={deactivateFloatingChat} className={iconButton} title="Close">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div ref={messagesContainerRef} className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden bg-[#f7f7f8] px-3 py-3">
        {loading ? (
          <div className="flex h-full items-center justify-center">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-200 border-t-gray-600" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center px-6 text-center">
            <Avatar person={other} group={isGroup} size={56} />
            <p className="mt-3 text-[14px] font-semibold text-gray-900">{title}</p>
            <p className="mt-1 text-[12.5px] text-gray-500">
              {isGroup ? 'No messages yet.' : `Say hi to ${firstName}. Messages also show up in FIN Chats.`}
            </p>
          </div>
        ) : (
          <div className="space-y-1">
            {messages.map((message, i) => {
              const prev = messages[i - 1];
              const next = messages[i + 1];
              const ms = (m?: Message) => m?.timestamp?.toMillis?.() || 0;
              const startsRun = !prev || prev.sender !== message.sender || ms(message) - ms(prev) > GROUP_GAP_MS;
              const endsRun = !next || next.sender !== message.sender || ms(next) - ms(message) > GROUP_GAP_MS;
              const isOwn = message.sender?.toLowerCase() === myEmail;
              return (
                <div key={message.id} className={cn(startsRun && i > 0 && 'pt-2')}>
                  <MessageBubble
                    message={message}
                    isOwn={isOwn}
                    showSender={isGroup && !isOwn && startsRun}
                    senderDisplayName={getDisplayName(message.sender)}
                    senderPhotoURL={people[message.sender?.toLowerCase()]?.photoURL}
                    showTimestamp={endsRun}
                    currentUserEmail={currentUser?.email || ''}
                    onEditMessage={(messageId, newText) => editMessage(messageId, newText)}
                    onReplyMessage={setReplyToMessage}
                    getDisplayName={getDisplayName}
                    compact={true}
                    chatId={chatId}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="shrink-0 border-t border-gray-100 bg-white">
        {replyToMessage && (
          <div className="mx-3 mt-2 flex items-center gap-2 rounded-lg bg-gray-50 py-1.5 pl-2.5 pr-1">
            <CornerUpLeft className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-medium text-gray-700">Replying to {getDisplayName(replyToMessage.sender)}</p>
              <p className="truncate text-[11.5px] text-gray-500">{replyToMessage.text || '📷 Image'}</p>
            </div>
            <button onClick={() => setReplyToMessage(null)} className="rounded-full p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-700" title="Cancel reply">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
        <MessageInput
          onSend={sendMessage}
          compact={true}
          chatId={chatId}
          currentUser={currentUser}
          placeholder={isGroup ? 'Message the group' : `Message ${firstName}`}
          autoFocus
        />
      </div>
    </div>
  );
};

export default FloatingChatWindow;
